import React from 'react';
import { HistoryEntry } from '../../types';
import { BackButton } from '../ui';
import DiceBackground from '../DiceBackground';
import { useTranslation } from '../../i18n';

interface HistoryProps {
  history: HistoryEntry[];
  loading: boolean;
  onBack: () => void;
}

export const History: React.FC<HistoryProps> = ({ history, loading, onBack }) => {
  const t = useTranslation();

  const outcomeLabel = (outcome: string) => {
    if (outcome === 'VICTORY') return t.victory;
    if (outcome === 'DEFEAT') return t.defeat;
    return t.draw;
  };

  return (
    <div className="h-[100dvh] flex flex-col items-center justify-start text-white p-4 pt-8 relative select-none bg-[#1a101f] overflow-hidden">
      <DiceBackground count={10} />

      <BackButton onClick={onBack} />

      <h1 className="text-2xl lg:text-5xl font-bold tracking-tighter mb-8 text-theme drop-shadow-lg uppercase text-center relative z-10 mt-4">{t.pitRecords}</h1>
      <div className="w-full max-w-2xl flex-1 flex flex-col gap-2 overflow-y-auto pr-2 custom-scrollbar relative z-10">
        {loading ? (
          <div className="p-8 pixel-border text-center text-gray-400 text-sm animate-pulse">{t.loadingDots}</div>
        ) : history.length === 0 ? (
          <div className="p-8 pixel-border text-center text-gray-500 text-sm italic">{t.noBattles}</div>
        ) : (
          history.map((entry) => (
            <div 
              key={entry.id} 
              className="pixel-card p-3 lg:p-5 flex flex-col sm:flex-row items-center justify-between gap-3 border-l-8"
              style={{ borderLeftColor: entry.outcome === 'VICTORY' ? '#22c55e' : entry.outcome === 'DEFEAT' ? '#ef4444' : '#eab308' }}
            >
              <div className="flex flex-col gap-1 items-center sm:items-start text-center sm:text-left min-w-0">
                <span className={`text-[10px] lg:text-sm font-bold ${entry.outcome === 'VICTORY' ? 'text-green-400' : entry.outcome === 'DEFEAT' ? 'text-red-400' : 'text-yellow-400'}`}>
                  {outcomeLabel(entry.outcome)}
                </span>
                {entry.opponentName && (
                  <span className="text-[10px] lg:text-xs text-gray-300 font-semibold tracking-wide truncate max-w-[180px]">
                    {t.vs} {entry.opponentName}
                  </span>
                )}
                <span className="text-[8px] lg:text-[10px] text-gray-500 uppercase">{entry.date}</span>
              </div>
              <div className="text-xs lg:text-xl font-bold tracking-widest flex gap-4 bg-black/40 px-3 lg:px-6 py-1.5 lg:py-3 pixel-border shrink-0">
                <span className="text-blue-400">{entry.playerScore}</span>
                <span className="text-gray-400 text-[8px] lg:text-xs mt-1">{t.vs}</span>
                <span className="text-red-400">{entry.opponentScore}</span>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
